import toast from 'react-hot-toast';
import notificationService from '../services/notificationService';

// Check if all notifications are disabled
const areAllNotificationsDisabled = () => {
  try {
    const subscriptions = notificationService.getLocalSubscriptions();
    const values = Object.values(subscriptions);

    // Check if user has explicitly disabled all notifications
    const globalDisabled = localStorage.getItem('notificationsGloballyDisabled') === 'true';

    // If no subscriptions data and not globally disabled, assume enabled
    if (values.length === 0 && !globalDisabled) return false;

    // If globally disabled, return true
    if (globalDisabled) return true;

    // If all category values are false, notifications are disabled
    return values.every(value => value === false);
  } catch (error) {
    console.error('Error checking notification status:', error);
    return false;
  }
};

// Check if specific category is enabled
const isCategoryEnabled = (category) => {
  if (!category) return true;
  return notificationService.isSubscribed(category);
};

// Main notification function that respects user settings
export const showNotification = (type, message, options = {}, category = null) => {
  // Check if all notifications are disabled
  if (areAllNotificationsDisabled()) {
    console.log('🔇 All notifications disabled, skipping:', message);
    return null;
  }

  // Check specific category
  if (category && !isCategoryEnabled(category)) {
    console.log(`🔇 Category '${category}' disabled, skipping:`, message);
    return null;
  }

  console.log(`🔔 Showing ${type} notification:`, message);

  // Show notification based on type
  switch (type) {
    case 'success':
      return toast.success(message, { duration: 4000, ...options });
    case 'error':
      return toast.error(message, { duration: 5000, ...options });
    case 'warning':
      return toast(message, { duration: 4000, icon: '⚠️', ...options });
    case 'info':
      return toast(message, { duration: 4000, icon: 'ℹ️', ...options });
    default:
      return toast(message, { duration: 4000, ...options });
  }
};

// Convenience functions
export const showSuccess = (message, options = {}, category = 'success') => {
  return showNotification('success', message, options, category);
};

export const showError = (message, options = {}, category = 'error') => {
  return showNotification('error', message, options, category);
};

export const showWarning = (message, options = {}, category = 'warning') => {
  return showNotification('warning', message, options, category);
};

export const showInfo = (message, options = {}, category = 'info') => {
  return showNotification('info', message, options, category);
};

// Special function for operation-specific notifications
export const showOperationNotification = (type, message, operation = 'general', options = {}) => {
  return showNotification(type, message, options, operation);
};

// Create a wrapper for the original toast to ensure all notifications go through our system
export const safeToast = {
  success: (message, options = {}) => showSuccess(message, options),
  error: (message, options = {}) => showError(message, options),
  warning: (message, options = {}) => showWarning(message, options),
  info: (message, options = {}) => showInfo(message, options),
  loading: (message, options = {}) => {
    // Loading notifications might be system critical, so we show them unless all are disabled
    if (areAllNotificationsDisabled()) return null;
    return toast.loading(message, options);
  },
  dismiss: (toastId) => toast.dismiss(toastId),
  remove: (toastId) => toast.remove(toastId)
};

// Export utility functions (single export statement)
export { areAllNotificationsDisabled, isCategoryEnabled };
